import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";

export default function StaffLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user !== false) navigate("/staff/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      navigate("/staff/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err, "Sign in failed. Check your email and password."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-ei-white">
      <Seo title="Staff sign in — EI Battery Solutions" />
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-ei-navy text-ei-white p-12">
        <div className="font-display text-lg font-bold">EI Battery Solutions</div>
        <div>
          <div className="ei-eyebrow text-ei-gold">Internal sales workspace</div>
          <h1 className="mt-4 text-4xl font-bold tracking-tight leading-tight">Quiet confidence.<br />Built with precision.</h1>
          <p className="mt-5 text-ei-white/70 max-w-md leading-relaxed">
            Authorised staff only. Manage wholesale enquiries, quotations and follow-ups from a single, secure workspace.
          </p>
        </div>
        <div className="text-xs text-ei-white/40">Private preview — not published to production.</div>
      </div>

      {/* Right form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 py-12">
        <div className="w-full max-w-sm mx-auto">
          <div className="lg:hidden mb-8"><Logo width={180} /></div>
          <div className="w-12 h-12 rounded-sm bg-ei-navy flex items-center justify-center">
            <Lock className="w-5 h-5 text-ei-gold" />
          </div>
          <h2 className="mt-5 text-2xl font-bold text-ei-navy">Sign in to the workspace</h2>
          <p className="mt-2 text-sm text-ei-graphite/70">Enter your staff credentials to continue.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="email" className="block font-display text-sm font-semibold text-ei-navy">Email</label>
              <input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 w-full min-h-[44px] rounded-sm border border-ei-mist bg-white px-3.5 py-2.5 text-ei-graphite focus:border-ei-navy outline-none" data-testid="login-email" autoComplete="username" />
            </div>
            <div>
              <label htmlFor="password" className="block font-display text-sm font-semibold text-ei-navy">Password</label>
              <input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 w-full min-h-[44px] rounded-sm border border-ei-mist bg-white px-3.5 py-2.5 text-ei-graphite focus:border-ei-navy outline-none" data-testid="login-password" autoComplete="current-password" />
            </div>
            {error && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2" role="alert" data-testid="login-error">{error}</div>
            )}
            <button type="submit" disabled={loading} className="w-full font-display text-sm font-bold bg-ei-navy text-ei-white px-6 py-3.5 rounded-sm hover:bg-ei-ink transition-colors min-h-[44px] inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="login-submit">
              {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Signing in…</>) : "Sign in"}
            </button>
          </form>

          <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-ei-graphite/70 hover:text-ei-navy" data-testid="login-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
        </div>
      </div>
    </div>
  );
}
